import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronDownIcon } from 'lucide-preact';
import { useState } from 'preact/hooks';

import { getProductsQueryOptions, postUserProductMutationFn } from '@/queries/products';
import { Product, ProductInterval } from '@/queries/products/types';
import { trackMP } from '@/shared/mp';

import { Button } from '../Buttons';
import ProductIntervalSelector from './ProductIntervalSelector';
import ProductPrice from './ProductPrice';

interface SponsorshipSubscriptionProps {
  habitatId: string;
  entryPoint?: string; // for analytics only
}

const getTierImageInfo = (lookupKey: string | undefined) => {
  const tier = parseInt(lookupKey?.split('-').at(-1) ?? '1', 10);
  switch (tier) {
    case 2:
      return {
        src: 'https://zoolife.tv/assets/products/l2-t2.webp',
        width: 62,
        height: 65,
        tier: 2,
      };
    case 3:
      return {
        src: 'https://zoolife.tv/assets/products/l2-t3.webp',
        width: 70,
        height: 69,
        tier: 3,
      };
    default:
      return {
        src: 'https://zoolife.tv/assets/products/l2-t1.webp',
        width: 55,
        height: 59,
        tier: 1,
      };
  }
};

const renderTierImage = (lookupKey: string | undefined) => {
  const { src, width, height, tier } = getTierImageInfo(lookupKey);
  return (
    <img src={src} alt={`Sponsorship Tier ${tier}`} width={width} height={height} className="preflight preflight-img" />
  );
};

const SponsorshipSubscription = ({ habitatId, entryPoint }: SponsorshipSubscriptionProps) => {
  const [productInterval, setProductInterval] = useState<ProductInterval>('month');
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);

  const { data: productsByInterval } = useQuery({
    ...getProductsQueryOptions({
      lookupKey:
        'monthly-supporter-tier-1,monthly-supporter-tier-2,monthly-supporter-tier-3,annual-supporter-tier-1,annual-supporter-tier-2,annual-supporter-tier-3',
    }),
    select: (data) => {
      const map = data.list.reduce((acc, product) => {
        if (!product.productLookupKey) return acc;
        acc.set(product.productLookupKey, product);
        return acc;
      }, new Map<string, Product>());
      return {
        month: [
          map.get('monthly-supporter-tier-1'),
          map.get('monthly-supporter-tier-2'),
          map.get('monthly-supporter-tier-3'),
        ].filter((product) => product != null),
        year: [
          map.get('annual-supporter-tier-1'),
          map.get('annual-supporter-tier-2'),
          map.get('annual-supporter-tier-3'),
        ].filter((product) => product != null),
      } as Partial<Record<ProductInterval, Product[]>>;
    },
  });

  const products = productsByInterval?.[productInterval];

  const { mutate: checkout, isPending } = useMutation({
    mutationFn: postUserProductMutationFn,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  return (
    <div className="w-full max-w-[336px] rounded-2xl shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]">
      <div className="relative w-full overflow-hidden rounded-2xl">
        {/* selector */}
        <div className="mt-5 flex w-full flex-col items-center gap-3">
          <div className="w-full px-3">
            <h4 className="preflight preflight-h4 text-center text-2xl/7 font-bold text-green-900">Sponsor Their Care</h4>
            <p className="preflight preflight-p mt-3 text-center text-sm text-stone-500">
              100% of your support* goes directly to their care. You’ll receive unlimited access to the habitat.
            </p>
          </div>
          <ProductIntervalSelector
            value={productInterval}
            onChange={(interval) => {
              setProduct(null);
              setProductInterval(interval);
            }}
          />
          <div className="grid w-full gap-2 px-2" style={{ gridTemplateColumns: `repeat(${products?.length ?? 0}, 1fr)` }}>
            {products?.map((p) => (
              <button
                role="option"
                type="button"
                aria-selected={p._id === product?._id}
                className="preflight preflight-button border-secondary-green/20 flex flex-col items-center justify-between gap-2 rounded-2xl border py-3 text-lg/none text-secondary-green aria-selected:border-secondary-green"
                key={p._id}
                onClick={() => setProduct(p)}
              >
                <span>
                  <ProductPrice currency={p.price.currency} price={p.price.price} className="text-base/none font-normal" />/
                  {productInterval === 'month' ? 'Mo' : 'Yr'}
                </span>
                {renderTierImage(p.productLookupKey)}
              </button>
            ))}
          </div>
          <div className="w-full px-6">
            <Button
              disabled={!product || isPending}
              size="manual"
              className="w-full px-4 py-3 text-base/none font-bold"
              onClick={() => {
                if (!product) return;
                checkout({
                  productId: product._id,
                  priceId: product.price.priceId,
                  associatedHabitatIds: [habitatId],
                });
                trackMP('request-checkout', {
                  productId: product._id,
                  priceId: product.price.priceId,
                  productLookupKey: product.productLookupKey,
                  habitatId,
                  entryPoint,
                });
              }}
            >
              Support Now
            </Button>
          </div>
          <button
            type="button"
            className="preflight preflight-button mb-6 mt-3 flex flex-col items-center justify-center text-sm text-stone-500"
            onClick={() => setShowLearnMore(!showLearnMore)}
          >
            <span>Where Do These Proceeds Go?</span>
            <ChevronDownIcon className="size-4" />
          </button>
        </div>

        {/* learn more */}
        <div
          aria-hidden={!showLearnMore}
          className="absolute inset-0 flex flex-col items-center bg-white transition-transform aria-hidden:pointer-events-none aria-hidden:translate-y-full"
        >
          <button
            type="button"
            className="preflight preflight-button mt-3 flex flex-col items-center justify-center text-sm text-stone-500"
            onClick={() => setShowLearnMore(!showLearnMore)}
          >
            <span className="sr-only">Dismiss Learn More</span>
            <ChevronDownIcon className="size-4 rotate-180" />
          </button>
          <div className="flex min-h-0 grow flex-col items-center justify-center px-4">
            <h4 className="preflight preflight-h4 text-center text-xl/6 font-bold text-green-900">How Sponsorship Works</h4>
            <p className="preflight preflight-p my-3 max-w-[240px] text-center text-sm text-stone-500">
              Funds collected for animal care are typically spent in these categories:
            </p>
            <img
              src="https://zoolife.tv/assets/products/sponsorship-how-it-works.webp"
              width={318}
              height={176}
              alt="Sponsorship How It Works"
              className="preflight preflight-img"
            />
            <p className="preflight preflight-p mt-3 text-center text-xs text-stone-500">
              * zoolife retains a small fee to cover payment processing, customer support & streaming costs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SponsorshipSubscription;
